import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  TextField,
  CircularProgress,
  Popper,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Paper,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { fetchSuggestions } from '../utils/api';

const MINIMUM_CHARACTER_SEARCH = 3;
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://spot.ul-dev.com';
const defaultImage = ``;


const StyledPopper = styled(Popper)(({ theme }) => ({
  zIndex: 1300,
  width: 400,
  maxHeight: 300,
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
}));

export default function MaterialAutoComplete() {
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const listRef = useRef(null);

  const fetchData = useCallback(async () => {
    if (query.length < MINIMUM_CHARACTER_SEARCH) {
      setOptions([]);
      setHasMore(false);
      return;
    }

    setLoading(true);
    const results = await fetchSuggestions(query, page);
    if (results.length > 0) {
      setOptions((prev) => (page === 1 ? results : [...prev, ...results]));
      setHasMore(true);
    } else {
      setHasMore(false);
    }
    setLoading(false);
  }, [query, page]);

  useEffect(() => {
    setPage(1);
    setOptions([]);
    setHasMore(true);
  }, [query]);

  useEffect(() => {
    const debounce = setTimeout(() => {
      fetchData();
    }, 300);
    return () => clearTimeout(debounce);
  }, [fetchData]);

  const handleScroll = () => {
    if (!listRef.current || loading || !hasMore) return;
  
    const { scrollTop, scrollHeight, clientHeight } = listRef.current;
    console.log('Scroll position:', scrollTop, scrollHeight, clientHeight);
  
    if (scrollHeight - scrollTop <= clientHeight + 50) {
      setPage((prev) => prev + 1);
    }
  };

  return (
    <div>
      <TextField
        label="Search Products"
        variant="outlined"
        fullWidth
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setAnchorEl(e.currentTarget);
        }}
        placeholder={`Type at least ${MINIMUM_CHARACTER_SEARCH} characters...`}
      />

      {anchorEl && options.length > 0 && (
        <StyledPopper open anchorEl={anchorEl} placement="bottom-start">
          <Paper ref={listRef} onScroll={handleScroll} style={{ maxHeight: 300, overflowY: 'auto' }}>
            <List>
              {options.map((item) => (
                <ListItem
                  key={item.id}
                  button
                  component="a"
                  href={`${API_BASE_URL}/main-app/products/detail/${item.id}?page_type=Products%20Catalog`}
                  target="_blank"
                >
                <ListItemAvatar>
                    <Avatar
                      src={item.thumbnail ? item.thumbnail : defaultImage}
                      alt={item.name}
                      className="me-2 rounded small-thumbnail"
                      width="40" // ✅ Make image smaller
                      height="40"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = defaultImage;
                      }}
                    />
                  </ListItemAvatar>
                  <ListItemText primary={item.name} />
                </ListItem>
              ))}
              {loading && (
                <ListItem>
                  <ListItemText
                    primary={<CircularProgress size={20} />}
                  />
                </ListItem>
              )}
              {!hasMore && (
                <ListItem>
                  <ListItemText primary="No more results" />
                </ListItem>
              )}
            </List>
          </Paper>
        </StyledPopper>
      )}
    </div>
  );
}